import React, { forwardRef, memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SignaturePad } from '../SignaturePad/SignaturePad';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '../../components/material-ui/Dialog';
import { DialogControls } from '../DialogControls/DialogControls';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import MaterialTextField from '../MaterialTextField/MaterialTextField';
import { Typography } from '../material-ui/Typography';
import { useStyles } from './useStyles';
import { useSignaturePopup } from './useSignaturePopup';

export const SignaturePopup = memo(
    forwardRef(({ showComment, onClose, title, visibility, onSign, signComment, ofpId }, ref) => {
        const classes = useStyles();
        const { t: translate } = useTranslation();

        const {
            comment,
            commentRef,
            signature,
            signatureEmpty,
            refueling,
            errorText,
            handleRefuelingChange,
            handleInputFocus,
            handleClearSignature,
            handleSign,
            handleClose,
            handleCommentChange,
            handleSetSignature,
            handleUpdateActions,
        } = useSignaturePopup(onClose, onSign, signComment, ofpId);

        const actions = useMemo(
            () => [
                { label: translate('cancel'), primary: true, onClick: handleClose },
                {
                    label: ofpId ? translate('send') : translate('ok'),
                    primary: true,
                    onClick: handleSign,
                    disabled: signatureEmpty || (ofpId && (!refueling || Boolean(errorText))),
                },
            ],
            [signatureEmpty, refueling, errorText, translate]
        );

        useEffect(() => {
            if (signature) {
                signature.addEventListener('afterUpdateStroke', handleUpdateActions);
            }

            return () => {
                if (signature) {
                    signature.removeEventListener('afterUpdateStroke', handleUpdateActions);
                }
            };
        });

        return (
            <Dialog open={visibility} onClose={onClose} fullWidth={true} maxWidth="lg">
                <DialogTitle className={classes.title}>{title}</DialogTitle>
                <DialogContent ref={ref}>
                    <ErrorBoundary>
                        <SignaturePad onClear={handleClearSignature} onInitSignature={handleSetSignature} />
                        <Typography className={classes.info}>{translate('messages.tapToConfirmSign')}</Typography>
                        {ofpId && (
                            <MaterialTextField
                                label={translate('labels.decisionFuel')}
                                name={'decisionFuel'}
                                fullWidth={true}
                                onChange={handleRefuelingChange}
                                value={refueling}
                                onFocus={handleInputFocus}
                                required={true}
                                className={classes.field}
                                type="number"
                                helperText={errorText}
                                error={Boolean(errorText)}
                            />
                        )}
                        {showComment && (
                            <MaterialTextField
                                inputRef={commentRef}
                                placeholder={translate('comment')}
                                label={translate('comment')}
                                name="signComment"
                                onChange={handleCommentChange}
                                fullWidth={true}
                                value={comment}
                                multiline={true}
                                minRows={1}
                                maxRows={2}
                                key="signComment"
                            />
                        )}
                    </ErrorBoundary>
                </DialogContent>
                <DialogActions className={classes.actions}>
                    <DialogControls actions={actions} />
                </DialogActions>
            </Dialog>
        );
    })
);
